import { useQuery } from 'react-query';
import {
	getAllApplications,
	getApplications,
	getMaxNumberOfRoundsForPosition,
	getOpenApplicationsCount,
	getUsersListForARound,
	getUserDashboard,
} from 'src/apis/application';
import {
	GetMyApplicationsAPIResponse,
	GetOpenApplicationsAPIResponse,
	GetAllApplicationsAPIResponse,
	GetMaxNumberOfRoundsAPIResponse,
	GetUsersListForARoundApiResponse,
	ApplicationRoundType,
} from 'types';

export const useGetMyApplications = () => {
	return useQuery({
		queryKey: ['getApplications'],
		queryFn: async () => (await getApplications()) as GetMyApplicationsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
	});
};
export const useGetAllApplications = ({ page, limit, showRecommendedOnly }: { page: string; limit: string; showRecommendedOnly?: string }) => {
	return useQuery({
		queryKey: ['getAllApplications', page, limit, showRecommendedOnly],
		queryFn: async () => (await getAllApplications({ page, limit, showRecommendedOnly })) as GetAllApplicationsAPIResponse,
		refetchInterval: 15000,
		staleTime: 0,
		refetchOnMount: true,
	});
};
export const useGetMyOpenApplicationsCount = (isJobseekerLoggedIn: boolean) => {
	return useQuery({
		queryKey: ['getOpenApplicationsCount'],
		queryFn: async () => (await getOpenApplicationsCount()) as GetOpenApplicationsAPIResponse,
		staleTime: 0,
		enabled: isJobseekerLoggedIn,
		refetchOnMount: true,
	});
};

export const useGetMaxNumberOfRoundsForPosition = (
	positionId: string,
	onComplete?: (data: GetMaxNumberOfRoundsAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getMaxNumberOfRoundsForPosition'],
		queryFn: async () => (await getMaxNumberOfRoundsForPosition(positionId)) as GetMaxNumberOfRoundsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		enabled: positionId.length > 0,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetUsersListForARound = (
	positionId: string,
	roundNumber: number,
	roundType?: ApplicationRoundType,
	onComplete?: (data: GetUsersListForARoundApiResponse) => void,
	search?: string,
	active?: boolean,
) => {
	return useQuery({
		queryKey: ['getUsersListForARound', positionId, roundNumber, roundType, search, active],
		queryFn: async () =>
			(await getUsersListForARound(
				positionId,
				roundNumber,
				roundType,
				search,
				active,
			)) as GetUsersListForARoundApiResponse,
		staleTime: 0,
		refetchOnMount: true,
		enabled: positionId.length > 0,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetUserDashboard = (params?: {
	fromDate?: string;
	toDate?: string;
	search?: string;
	page?: number;
	limit?: number;
}) => {
	// Create a stable query key with explicit values
	const queryKey: [
		string,
		string | undefined,
		string | undefined,
		string | undefined,
		number | undefined,
		number | undefined,
	] = [
			'getUserDashboard',
			params?.fromDate,
			params?.toDate,
			params?.search,
			params?.page,
			params?.limit,
		];

	return useQuery({
		queryKey,
		queryFn: async () => {
			// Extract params from query key to ensure we always use the current values
			const [, fromDate, toDate, search, page, limit] = queryKey;

			// Validate page number before making the call
			if (typeof page !== 'number' || page < 1) {
				console.warn('[Frontend] Invalid page number in query, skipping API call:', { page, queryKey });
				throw new Error(`Invalid page number: ${page}`);
			}

			return await getUserDashboard({
				fromDate,
				toDate,
				search,
				page,
				limit,
			});
		},
		// Prevent double calls and race conditions
		staleTime: 0, // Always consider data stale to ensure fresh fetches
		cacheTime: 0, // Don't cache - immediately remove unused queries
		keepPreviousData: false, // Don't keep previous data when query key changes
		refetchOnMount: false, // Don't refetch on mount
		refetchOnWindowFocus: false, // Don't refetch on window focus
		refetchOnReconnect: false, // Don't refetch on reconnect
		placeholderData: undefined, // No placeholder data
		// Cancel in-flight requests when query key changes
		structuralSharing: false, // Disable structural sharing to prevent stale data
		// Only enable the query if we have valid params
		enabled: typeof params?.page === 'number' && params.page >= 1,
	});
};
